import { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Timer, Play, Pause, RotateCcw, Copy, Link, Plus } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const PRESETS = [
  { label: "1 hr", seconds: 3600 },
  { label: "45 min", seconds: 2700 },
  { label: "30 min", seconds: 1800 },
  { label: "15 min", seconds: 900 },
  { label: "5 min", seconds: 300 },
  { label: "1 min", seconds: 60 },
];

const secondsToHMS = (total) => ({
  hours: String(Math.floor(total / 3600)),
  minutes: String(Math.floor((total % 3600) / 60)),
  seconds: String(total % 60),
});

export const SyncTimer = () => {
  const [name, setName] = useState("");
  const [hours, setHours] = useState("0");
  const [minutes, setMinutes] = useState("30");
  const [seconds, setSeconds] = useState("0");
  const [session, setSession] = useState(null);
  const [displayTime, setDisplayTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [joinCode, setJoinCode] = useState("");

  const totalInputSeconds =
    (parseInt(hours) || 0) * 3600 + (parseInt(minutes) || 0) * 60 + (parseInt(seconds) || 0);

  const applyPreset = (presetSeconds) => {
    const { hours: h, minutes: m, seconds: s } = secondsToHMS(presetSeconds);
    setHours(h);
    setMinutes(m);
    setSeconds(s);
  };

  const createSession = async () => {
    const totalSeconds = parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds);
    if (totalSeconds <= 0) {
      toast.error("Please set a timer duration");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/timer/sessions`, {
        name: name || "Timer Session",
        duration_seconds: totalSeconds
      });
      setSession(response.data);
      setDisplayTime(response.data.remaining_seconds);
      toast.success(`Session created! Code: ${response.data.code}`);
    } catch (error) {
      toast.error("Failed to create session");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const joinSession = async () => {
    if (!joinCode) {
      toast.error("Please enter a session code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/timer/sessions/${joinCode}`);
      setSession(response.data);
      setDisplayTime(response.data.remaining_seconds || response.data.duration_seconds);
      toast.success("Joined session!");
    } catch (error) {
      toast.error("Session not found");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const controlTimer = async (action) => {
    if (!session) return;
    
    try {
      const response = await axios.put(`${API}/timer/sessions/${session.code}`, { action });
      setSession(response.data);
      if (action === "reset") {
        setDisplayTime(response.data.duration_seconds);
      }
      toast.success(`Timer ${action}${action === 'reset' ? '' : 'ed'}`);
    } catch (error) {
      toast.error(`Failed to ${action} timer`);
      console.error(error);
    }
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/execution/timer/${session.code}`;
    navigator.clipboard.writeText(link);
    toast.success("Share link copied!");
  };

  const copyCode = () => {
    navigator.clipboard.writeText(session.code);
    toast.success("Code copied!");
  };

  // Timer countdown effect
  useEffect(() => {
    let interval;
    if (session?.is_running && displayTime > 0) {
      interval = setInterval(() => {
        setDisplayTime((prev) => {
          if (prev <= 1) {
            controlTimer("pause");
            toast.success("Timer finished!", { duration: 5000 });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [session?.is_running, displayTime]);

  // Sync with server periodically
  const syncWithServer = useCallback(async () => {
    if (!session) return;
    try {
      const response = await axios.get(`${API}/timer/sessions/${session.code}`);
      setSession(response.data);
      
      if (response.data.is_running && response.data.started_at) {
        const started = new Date(response.data.started_at).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - started) / 1000);
        const remaining = Math.max(0, (response.data.remaining_seconds || response.data.duration_seconds) - elapsed);
        setDisplayTime(remaining);
      } else if (!response.data.is_running) {
        setDisplayTime(response.data.remaining_seconds || response.data.duration_seconds);
      }
    } catch (error) {
      console.error("Sync failed:", error);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      const syncInterval = setInterval(syncWithServer, 5000);
      return () => clearInterval(syncInterval);
    }
  }, [session, syncWithServer]);

  return (
    <Layout 
      title="Sync Timer" 
      subtitle="Create shareable countdown timers for your team"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {!session ? (
          <>
            {/* Create Session Card */}
            <Card className="tactical-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg uppercase">
                  <Plus className="h-5 w-5 text-yellow-400" />
                  Create Timer
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="label-tactical">Session Name</Label>
                  <Input
                    id="name"
                    placeholder="Keynote Speech"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="tactical-input"
                    data-testid="session-name-input"
                  />
                </div>

                {/* Quick presets */}
                <div className="space-y-2">
                  <Label className="label-tactical">Quick Presets</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="timer-presets">
                    {PRESETS.map((p) => {
                      const isActive = totalInputSeconds === p.seconds;
                      return (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => applyPreset(p.seconds)}
                          className={`px-3 py-2 rounded-sm border text-sm font-medium transition-colors ${
                            isActive
                              ? "bg-accent text-accent-foreground border-accent"
                              : "bg-secondary/40 border-border hover:border-foreground/30"
                          }`}
                          data-testid={`timer-preset-${p.seconds}`}
                          aria-pressed={isActive}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tap a preset or set a custom time below.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="label-tactical">Hours</Label>
                    <Input
                      type="number"
                      min="0"
                      max="23"
                      value={hours}
                      onChange={(e) => setHours(e.target.value)}
                      className="tactical-input text-center"
                      data-testid="hours-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-tactical">Minutes</Label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={minutes}
                      onChange={(e) => setMinutes(e.target.value)}
                      className="tactical-input text-center"
                      data-testid="minutes-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-tactical">Seconds</Label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={seconds}
                      onChange={(e) => setSeconds(e.target.value)}
                      className="tactical-input text-center"
                      data-testid="seconds-input"
                    />
                  </div>
                </div>

                <Button 
                  onClick={createSession} 
                  className="tactical-btn w-full bg-yellow-600 hover:bg-yellow-700"
                  disabled={loading}
                  data-testid="create-session-button"
                >
                  {loading ? "Creating..." : "Create Session"}
                </Button>
              </CardContent>
            </Card>

            {/* Join Session Card */}
            <Card className="tactical-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg uppercase">
                  <Link className="h-5 w-5 text-yellow-400" />
                  Join Existing Session
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code" className="label-tactical">Session Code</Label>
                  <Input
                    id="code"
                    placeholder="ABC123"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    className="tactical-input text-center text-2xl tracking-[0.5em] uppercase"
                    maxLength={6}
                    data-testid="join-code-input"
                  />
                </div>
                <Button 
                  onClick={joinSession} 
                  variant="outline"
                  className="tactical-btn w-full"
                  disabled={loading}
                  data-testid="join-session-button"
                >
                  {loading ? "Joining..." : "Join Session"}
                </Button>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Timer Display */}
            <Card className={`tactical-card ${displayTime <= 60 && displayTime > 0 ? 'border-red-500/50' : 'border-yellow-500/30'}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg uppercase text-yellow-400">
                    {session.name}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={copyCode}
                      className="h-8"
                      data-testid="copy-code-button"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      {session.code}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={copyShareLink}
                      className="h-8"
                      data-testid="share-link-button"
                    >
                      <Link className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Timer Display */}
                <div className="text-center py-8">
                  <p className={`timer-huge ${displayTime <= 60 && displayTime > 0 ? 'text-red-500 animate-recording' : 'text-yellow-400'}`} data-testid="timer-display">
                    {formatTime(displayTime)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {session.is_running ? "Running" : displayTime === 0 ? "Finished" : "Paused"}
                  </p>
                </div>

                {/* Controls */}
                <div className="flex justify-center gap-4">
                  {!session.is_running ? (
                    <Button 
                      onClick={() => controlTimer("start")}
                      className="tactical-btn bg-green-600 hover:bg-green-700 w-32"
                      disabled={displayTime === 0}
                      data-testid="start-button"
                    >
                      <Play className="h-5 w-5 mr-2" />
                      Start
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => controlTimer("pause")}
                      className="tactical-btn bg-yellow-600 hover:bg-yellow-700 w-32"
                      data-testid="pause-button"
                    >
                      <Pause className="h-5 w-5 mr-2" />
                      Pause
                    </Button>
                  )}
                  <Button 
                    onClick={() => controlTimer("reset")}
                    variant="outline"
                    className="tactical-btn w-32"
                    data-testid="reset-button"
                  >
                    <RotateCcw className="h-5 w-5 mr-2" />
                    Reset
                  </Button>
                </div>

                {/* New Session */}
                <div className="pt-4 border-t border-border/50">
                  <Button 
                    variant="ghost" 
                    onClick={() => setSession(null)}
                    className="w-full text-muted-foreground hover:text-foreground"
                    data-testid="new-session-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Session
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Share Info */}
            <Card className="tactical-card">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground text-center">
                  Share this code with your team: <span className="font-mono font-bold text-yellow-400">{session.code}</span>
                </p>
                <p className="text-xs text-muted-foreground text-center mt-1">
                  Or share the direct link to sync timers across devices
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SyncTimer;
